import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Eye, EyeOff, Shuffle } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import { useToast } from '../Toast';
import { tiposUsuario, validarEmail, validarSenha, gerarSenhaAleatoria } from '../../services/usuarios';
const UsuarioForm = ({ usuario, onSubmit, onCancel, isLoading = false }) => {
    const { showToast } = useToast();
    const isEdit = !!usuario;
    const [formData, setFormData] = useState({
        nome: usuario?.nome || '',
        email: usuario?.email || '',
        password: '',
        tipo: usuario?.tipo || 'TECNICO',
        ativo: usuario?.ativo ?? true
    });
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Limpar erro do campo quando usuário começar a digitar
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };
    const gerarSenha = () => {
        const novaSenha = gerarSenhaAleatoria();
        setFormData(prev => ({ ...prev, password: novaSenha }));
        setShowPassword(true);
        showToast('Senha gerada automaticamente', 'success');
    };
    const validateForm = () => {
        const newErrors = {};
        // Validar nome
        if (!formData.nome.trim()) {
            newErrors.nome = 'Nome é obrigatório';
        }
        else if (formData.nome.trim().length < 2) {
            newErrors.nome = 'Nome deve ter pelo menos 2 caracteres';
        }
        // Validar email
        if (!formData.email.trim()) {
            newErrors.email = 'Email é obrigatório';
        }
        else if (!validarEmail(formData.email)) {
            newErrors.email = 'Email inválido';
        }
        // Validar senha (obrigatória para criação, opcional para edição)
        if (!isEdit && !formData.password) {
            newErrors.password = 'Senha é obrigatória';
        }
        else if (formData.password) {
            const validacaoSenha = validarSenha(formData.password);
            if (!validacaoSenha.valida) {
                newErrors.password = validacaoSenha.mensagem || 'Senha inválida';
            }
        }
        // Validar tipo
        if (!formData.tipo) {
            newErrors.tipo = 'Tipo de utilizador é obrigatório';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }
        try {
            // Preparar dados para envio
            const submitData = {
                nome: formData.nome.trim(),
                email: formData.email.trim(),
                tipo: formData.tipo,
                ativo: formData.ativo
            };
            // Incluir senha apenas se foi fornecida
            if (formData.password) {
                submitData.password = formData.password;
            }
            await onSubmit(submitData);
        }
        catch (error) {
            console.error('Erro ao salvar utilizador:', error);
            showToast(error.message || 'Erro ao salvar utilizador', 'error');
        }
    };
    return (_jsxs("form", { onSubmit: handleSubmit, className: "space-y-4", children: [_jsx(Input, { label: "Nome completo", value: formData.nome, onChange: (e) => handleChange('nome', e.target.value), error: errors.nome, placeholder: "Digite o nome completo", required: true }), _jsx(Input, { label: "Email", type: "email", value: formData.email, onChange: (e) => handleChange('email', e.target.value), error: errors.email, placeholder: "utilizador@exemplo.com", required: true }), _jsxs("div", { className: "space-y-2", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("label", { className: "block text-sm font-medium text-gray-700", children: ["Senha ", !isEdit && _jsx("span", { className: "text-red-500", children: "*" })] }), _jsx(Button, { type: "button", variant: "ghost", size: "sm", onClick: gerarSenha, leftIcon: _jsx(Shuffle, { className: "h-3 w-3" }), children: "Gerar" })] }), _jsx("div", { className: "relative", children: _jsx(Input, { type: showPassword ? 'text' : 'password', value: formData.password, onChange: (e) => handleChange('password', e.target.value), error: errors.password, placeholder: isEdit ? 'Deixe vazio para manter a senha atual' : 'Digite a senha', rightIcon: _jsx("button", { type: "button", onClick: () => setShowPassword(!showPassword), className: "text-gray-400 hover:text-gray-600", children: showPassword ? _jsx(EyeOff, { className: "h-4 w-4" }) : _jsx(Eye, { className: "h-4 w-4" }) }) }) }), !isEdit && (_jsx("p", { className: "text-xs text-gray-500", children: "A senha deve ter pelo menos 6 caracteres, incluindo mai\u00FAscula, min\u00FAscula e n\u00FAmero" }))] }), _jsx(Select, { label: "Tipo de Utilizador", value: formData.tipo, onChange: (e) => handleChange('tipo', e.target.value), error: errors.tipo, options: tiposUsuario.map(tipo => ({
                    value: tipo.value,
                    label: `${tipo.icon} ${tipo.label}`
                })), required: true }), isEdit && (_jsxs("div", { className: "flex items-center space-x-2", children: [_jsx("input", { type: "checkbox", id: "ativo", checked: formData.ativo, onChange: (e) => handleChange('ativo', e.target.checked), className: "rounded border-gray-300 text-primary-600 focus:ring-primary-500" }), _jsx("label", { htmlFor: "ativo", className: "text-sm font-medium text-gray-700", children: "Utilizador ativo" })] })), _jsxs("div", { className: "flex justify-end space-x-3 pt-4 border-t", children: [_jsx(Button, { type: "button", variant: "outline", onClick: onCancel, disabled: isLoading, children: "Cancelar" }), _jsx(Button, { type: "submit", isLoading: isLoading, loadingText: isEdit ? 'Guardando...' : 'Criando...', children: isEdit ? 'Guardar Alterações' : 'Criar Utilizador' })] })] }));
};
export default UsuarioForm;
